import type {
  SignalCategoryType,
  SignalStageType,
  SignalStatusType,
} from '@terra/shared-schemas-orchestrator';
import type { SignalDto } from '@terra/shared-contracts-signals';
import { isUserCreatedSignalOrigin } from '@terra/shared-contracts-signals';
import { isEqual } from 'radash';

export type SignalEditableFields = {
  description: string | null;
  analysis: string | null;
  confidence: number | null;
  reason: string | null;
  category: SignalCategoryType;
  stage: SignalStageType | null;
  status: SignalStatusType | null;
};

export const createEditableFieldsFromSignal = (
  signal: Pick<
    SignalDto,
    | 'description'
    | 'analysis'
    | 'confidence'
    | 'reason'
    | 'category'
    | 'stage'
    | 'status'
  >,
): SignalEditableFields => ({
  description: signal.description ?? null,
  analysis: signal.analysis ?? null,
  confidence: signal.confidence ?? null,
  reason: signal.reason ?? null,
  category: signal.category,
  stage: signal.stage ?? null,
  status: signal.status ?? null,
});

export const isUserCreatedSignal = ({
  origin,
}: Pick<SignalDto, 'origin'>): boolean => isUserCreatedSignalOrigin(origin);

export type SignalEditableFieldKey = keyof SignalEditableFields;

const EDITABLE_FIELD_KEYS = [
  'description',
  'analysis',
  'confidence',
  'reason',
  'category',
  'stage',
  'status',
] as const satisfies readonly SignalEditableFieldKey[];

const normalizeNullableString = (value: string | null): string | null =>
  value === '' ? null : value;

const buildChangedFieldUpdate = ({
  key,
  editableFields,
}: {
  key: SignalEditableFieldKey;
  editableFields: SignalEditableFields;
}): Partial<SignalEditableFields> => {
  switch (key) {
    case 'description':
      return {
        description: normalizeNullableString(editableFields.description),
      };
    case 'analysis':
      return { analysis: normalizeNullableString(editableFields.analysis) };
    case 'reason':
      return { reason: normalizeNullableString(editableFields.reason) };
    case 'confidence':
      return { confidence: editableFields.confidence };
    case 'category':
      return { category: editableFields.category };
    case 'stage':
      return editableFields.stage ? { stage: editableFields.stage } : {};
    case 'status':
      return { status: editableFields.status };
  }
};

export const getChangedSignalEditableFieldKeys = ({
  editableFields,
  originalFields,
}: {
  editableFields: SignalEditableFields;
  originalFields: SignalEditableFields;
}): SignalEditableFieldKey[] =>
  EDITABLE_FIELD_KEYS.filter(
    (key) => !isEqual(editableFields[key], originalFields[key]),
  );

export const hasSignalEditableFieldChanges = ({
  editableFields,
  originalFields,
}: {
  editableFields: SignalEditableFields;
  originalFields: SignalEditableFields;
}): boolean =>
  getChangedSignalEditableFieldKeys({ editableFields, originalFields }).length >
  0;

export const buildSignalUpdatePayload = ({
  editableFields,
  originalFields,
}: {
  editableFields: SignalEditableFields;
  originalFields: SignalEditableFields;
}): Partial<SignalEditableFields> => {
  const changedKeys = getChangedSignalEditableFieldKeys({
    editableFields,
    originalFields,
  });

  return changedKeys.reduce<Partial<SignalEditableFields>>(
    (accumulator, key) => ({
      ...accumulator,
      ...buildChangedFieldUpdate({ key, editableFields }),
    }),
    {},
  );
};

export const applyEditableFieldsToSignal = (
  signal: SignalDto,
  fields: Partial<SignalEditableFields>,
): SignalDto => ({
  ...signal,
  ...(fields.description !== undefined
    ? { description: fields.description }
    : {}),
  ...(fields.analysis !== undefined ? { analysis: fields.analysis } : {}),
  ...(fields.confidence !== undefined ? { confidence: fields.confidence } : {}),
  ...(fields.reason !== undefined ? { reason: fields.reason } : {}),
  ...(fields.category !== undefined ? { category: fields.category } : {}),
  ...(fields.stage != null ? { stage: fields.stage } : {}),
  ...(fields.status !== undefined ? { status: fields.status } : {}),
});
